import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://virtualpeek.fr";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

/**
 * SEO — dynamic meta tags per page.
 *
 * Props:
 *  - title (string, required)
 *  - description (string, required)
 *  - path (string): canonical path, default "/"
 *  - image (string): absolute URL for OG image
 *  - noindex (bool)
 *  - type (string): og:type, default "website" ("article" for blog posts)
 *  - jsonLd (object | object[]): extra schema.org JSON-LD blocks
 *  - breadcrumbs ([{name, path}]): auto-generates a BreadcrumbList schema
 *  - keywords (string): meta keywords (optional, used by Bing & local SE)
 */
export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  noindex = false,
  type = "website",
  jsonLd,
  breadcrumbs,
  keywords,
}) {
  const fullUrl = `${SITE_URL}${path}`;
  const absoluteImage = image && image.startsWith("http") ? image : `${SITE_URL}${image || "/og-image.jpg"}`;

  const schemas = [];
  if (Array.isArray(jsonLd)) schemas.push(...jsonLd);
  else if (jsonLd) schemas.push(jsonLd);

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${SITE_URL}${b.path}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={fullUrl} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="VirtualPeek" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
